import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled as isMobileFeatureFlagEnabled } from '@mobileStore/featureFlags/store';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { DateTime } from 'luxon';

/**
 * Pure (store-independent) chronology rules for driver In/Out times on a
 * load's stops. Lives in `@common/order` so it can be consumed by both UI
 * components and store/utils layers without creating a
 * `store -> utils -> views -> store` import cycle.
 *
 * Store-bound adapters that read the active load from `orderStore` live in
 * `src/views/order/components/carrier/stops/StopHelpers.ts` and forward to
 * the pure functions defined here.
 */

const MODES_FOR_AUTO_STATUS = ['TL', 'Partial'] as const;

export type DriverTimeField = 'driverInTime' | 'driverOutTime';

export type StopTimeValidationResult =
  | { ok: true }
  | { ok: false; message: string };

export interface StopChronologyViolation {
  loadIndex: number;
  stopIndex: number;
  position: number;
  field: DriverTimeField;
  message: string;
}

/** Minimal load shape consumed by `collectStopChronologyViolationsFor`. */
export interface ChronologyLoadInput {
  mode?: string | null;
  stops?: LastUsedTopStopModel[] | null;
}

export const BLOCK_INTRA_STOP = 'Driver Out time must be after Driver In time.';
export const BLOCK_INTER_PREV_OUT =
  'Driver In Time must be after previous stop Driver Out time.';
export const BLOCK_MISSING_IN_FOR_OUT =
  'Driver In time is required before Driver Out time can be set.';
export const BLOCK_MISSING_PRIOR_STOP =
  'Previous stop must have Driver In and Out times before this stop.';

const toUtcDateTime = (iso: string | null | undefined): DateTime | null => {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, { zone: 'utc' });
  return dt.isValid ? dt : null;
};

/** Indices of non-deleted stops, in their current order. */
export const getActiveIndices = (stops: LastUsedTopStopModel[]): number[] => {
  const indices: number[] = [];
  stops.forEach((s, i) => {
    if (s.operationType !== 'Delete') indices.push(i);
  });
  return indices;
};

/**
 * True when every active stop preceding `position` has both `driverInTime`
 * and `driverOutTime` populated.
 */
export const arePriorStopsComplete = (
  position: number,
  stops: LastUsedTopStopModel[],
  activeIndices: number[],
): boolean => {
  for (let i = 0; i < position; i++) {
    const prev = stops[activeIndices[i]];
    if (!prev.driverInTime || !prev.driverOutTime) return false;
  }
  return true;
};

/**
 * Shared position-based chronology check. `position` may equal
 * `activeIndices.length` for an appended new stop.
 */
const validateChronologyAtPosition = (
  stops: LastUsedTopStopModel[],
  activeIndices: number[],
  position: number,
  field: DriverTimeField,
  newIso: string | null,
  ownPartnerIso: string | null | undefined,
): StopTimeValidationResult => {
  const newDt = toUtcDateTime(newIso);
  if (!newDt) return { ok: true };

  if (field === 'driverInTime') {
    if (position > 0 && position <= activeIndices.length) {
      const prev = stops[activeIndices[position - 1]];
      const prevOut = toUtcDateTime(prev.driverOutTime);
      if (prevOut && newDt <= prevOut) {
        return { ok: false, message: BLOCK_INTER_PREV_OUT };
      }
    }
    const ownOut = toUtcDateTime(ownPartnerIso);
    if (ownOut && newDt >= ownOut) {
      return { ok: false, message: BLOCK_INTRA_STOP };
    }
  } else {
    const ownIn = toUtcDateTime(ownPartnerIso);
    if (ownIn && newDt <= ownIn) {
      return { ok: false, message: BLOCK_INTRA_STOP };
    }
    // Inter-stop chronology (out vs next stop's in) is enforced from the
    // next stop's Driver In side only, per ticket AC.
  }
  return { ok: true };
};

/** Position of `stopId` in the active-stop ordering, or appended-end. */
export const positionForStopIdIn = (
  stops: LastUsedTopStopModel[],
  activeIndices: number[],
  stopId: number | null | undefined,
): number => {
  if (stopId === null || stopId === undefined) return activeIndices.length;
  const idx = stops.findIndex((s) => s.id === stopId);
  if (idx === -1) return activeIndices.length;
  const pos = activeIndices.indexOf(idx);
  return pos === -1 ? activeIndices.length : pos;
};

export const isWaterfallActiveForMode = (
  mode: string | null | undefined,
): boolean => {
  const flagEnabled =
    isFeatureFlagEnabled(FeatureFlags.permission_auto_update_load_status)() ||
    isMobileFeatureFlagEnabled(
      FeatureFlags.permission_auto_update_load_status,
    )();
  if (!flagEnabled) {
    return false;
  }
  return MODES_FOR_AUTO_STATUS.includes(
    mode as (typeof MODES_FOR_AUTO_STATUS)[number],
  );
};

/**
 * Driver In for stop at `stopIndex` is editable when every prior active stop
 * has both Driver In and Driver Out populated.
 */
export const canEditDriverInTimeFor = (
  stops: LastUsedTopStopModel[],
  stopIndex: number,
  mode: string | null | undefined,
): boolean => {
  if (!isWaterfallActiveForMode(mode)) return true;
  const activeIndices = getActiveIndices(stops);
  const position = activeIndices.indexOf(stopIndex);
  if (position <= 0) return true;
  return arePriorStopsComplete(position, stops, activeIndices);
};

/**
 * Driver Out for stop at `stopIndex` is editable only when this stop has a
 * Driver In timestamp AND every prior active stop is complete.
 */
export const canEditDriverOutTimeFor = (
  stops: LastUsedTopStopModel[],
  stopIndex: number,
  mode: string | null | undefined,
  candidateInIso?: string | null,
): boolean => {
  if (!isWaterfallActiveForMode(mode)) return true;
  const inIso = candidateInIso ?? stops[stopIndex]?.driverInTime;
  if (!inIso) return false;
  const activeIndices = getActiveIndices(stops);
  const position = activeIndices.indexOf(stopIndex);
  if (position <= 0) return true;
  return arePriorStopsComplete(position, stops, activeIndices);
};

/**
 * Validates a candidate Driver In/Out timestamp for the stop at `stopIndex`
 * against intra- and inter-stop chronology rules.
 */
export const validateStopTimeChangeFor = (
  stops: LastUsedTopStopModel[],
  stopIndex: number,
  mode: string | null | undefined,
  field: DriverTimeField,
  newIso: string | null,
  ownPartnerIso: string | null | undefined,
): StopTimeValidationResult => {
  if (!isWaterfallActiveForMode(mode)) return { ok: true };
  const activeIndices = getActiveIndices(stops);
  return validateChronologyAtPosition(
    stops,
    activeIndices,
    activeIndices.indexOf(stopIndex),
    field,
    newIso,
    ownPartnerIso,
  );
};

/** Position-based variant for the stop editor modal (new-stop append). */
export const validateStopTimeChangeAtPositionFor = (
  stops: LastUsedTopStopModel[],
  mode: string | null | undefined,
  position: number,
  field: DriverTimeField,
  newIso: string | null,
  ownPartnerIso: string | null | undefined,
): StopTimeValidationResult => {
  if (!isWaterfallActiveForMode(mode)) return { ok: true };
  return validateChronologyAtPosition(
    stops,
    getActiveIndices(stops),
    position,
    field,
    newIso,
    ownPartnerIso,
  );
};

/**
 * Returns the first chronology violation for the stop at `stopIndex`, or
 * `null` if none. Pure save-time guard for a single stop in a provided list.
 */
export const findStopChronologyViolationFor = (
  stops: LastUsedTopStopModel[],
  stopIndex: number,
  mode: string | null | undefined,
): { field: DriverTimeField; message: string } | null => {
  if (!isWaterfallActiveForMode(mode)) return null;
  const activeIndices = getActiveIndices(stops);
  const position = activeIndices.indexOf(stopIndex);
  if (position === -1) return null;
  const stop = stops[stopIndex];
  const inDt = toUtcDateTime(stop.driverInTime);
  const outDt = toUtcDateTime(stop.driverOutTime);

  if (outDt && !inDt) {
    return { field: 'driverInTime', message: BLOCK_MISSING_IN_FOR_OUT };
  }
  if ((inDt || outDt) && position > 0) {
    for (let i = 0; i < position; i++) {
      const priorStop = stops[activeIndices[i]];
      const priorIn = toUtcDateTime(priorStop.driverInTime);
      const priorOut = toUtcDateTime(priorStop.driverOutTime);
      if (!priorIn || !priorOut) {
        return { field: 'driverInTime', message: BLOCK_MISSING_PRIOR_STOP };
      }
    }
  }
  if (inDt && outDt && outDt <= inDt) {
    return { field: 'driverOutTime', message: BLOCK_INTRA_STOP };
  }
  if (inDt && position > 0) {
    const prev = stops[activeIndices[position - 1]];
    const prevOut = toUtcDateTime(prev.driverOutTime);
    if (prevOut && inDt <= prevOut) {
      return { field: 'driverInTime', message: BLOCK_INTER_PREV_OUT };
    }
  }
  return null;
};

/**
 * Save-time guard: walks every load and returns every stop chronology
 * violation across the supplied list of loads. Returns an empty array when
 * the auto update feature flag is disabled.
 */
export const collectStopChronologyViolationsFor = (
  loads: ChronologyLoadInput[],
): StopChronologyViolation[] => {
  const violations: StopChronologyViolation[] = [];
  loads.forEach((load, loadIndex) => {
    if (!isWaterfallActiveForMode(load.mode)) return;
    const stops = load.stops ?? [];
    const activeIndices = getActiveIndices(stops);
    activeIndices.forEach((stopIndex, position) => {
      const stop = stops[stopIndex];
      const inDt = toUtcDateTime(stop.driverInTime);
      const outDt = toUtcDateTime(stop.driverOutTime);

      if (outDt && !inDt) {
        violations.push({
          loadIndex,
          stopIndex,
          position,
          field: 'driverInTime',
          message: BLOCK_MISSING_IN_FOR_OUT,
        });
      }

      if ((inDt || outDt) && position > 0) {
        for (let i = 0; i < position; i++) {
          const priorStop = stops[activeIndices[i]];
          const priorIn = toUtcDateTime(priorStop.driverInTime);
          const priorOut = toUtcDateTime(priorStop.driverOutTime);
          if (!priorIn || !priorOut) {
            violations.push({
              loadIndex,
              stopIndex,
              position,
              field: 'driverInTime',
              message: BLOCK_MISSING_PRIOR_STOP,
            });
            break;
          }
        }
      }

      // Intra-stop: Out must be strictly after In on the same stop.
      if (inDt && outDt && outDt <= inDt) {
        violations.push({
          loadIndex,
          stopIndex,
          position,
          field: 'driverOutTime',
          message: BLOCK_INTRA_STOP,
        });
      }

      // Inter-stop: this stop's In must be strictly after previous stop's Out.
      if (inDt && position > 0) {
        const prev = stops[activeIndices[position - 1]];
        const prevOut = toUtcDateTime(prev.driverOutTime);
        if (prevOut && inDt <= prevOut) {
          violations.push({
            loadIndex,
            stopIndex,
            position,
            field: 'driverInTime',
            message: BLOCK_INTER_PREV_OUT,
          });
        }
      }
    });
  });
  return violations;
};
